import React from 'react';
import { Button, Icons } from '@ohif/ui-next';
import { useSegmentationTableContext, useSegmentationExpanded } from './contexts';
import { useTranslation } from 'react-i18next';

function hasContourData(contourRepresentationData: any): boolean {
  if (!contourRepresentationData?.annotationUIDsMap) {
    return false;
  }

  return contourRepresentationData.annotationUIDsMap.size > 0;
}

export const AddSegmentRow: React.FC<{ children?: React.ReactNode }> = ({ children = null }) => {
  const { t } = useTranslation('SegmentationPanel');
  const {
    activeRepresentation,
    disableEditing,
    activeSegmentationId,
    onSegmentAdd,
    onShowSegmentationInViewport,
    onApplyTrenTranslation,
    onToggleSegmentationRepresentationVisibility,
    data,
    showAddSegment,
  } = useSegmentationTableContext('AddSegmentRow');

  // Try to get from expanded context first, then fall back to active segmentation
  let segmentationId = activeSegmentationId;
  let representation = activeRepresentation;

  try {
    const expandedContext = useSegmentationExpanded('AddSegmentRow');
    if (expandedContext.isActive) {
      segmentationId = expandedContext.segmentation.segmentationId;
      representation = expandedContext.representation;
    }
  } catch (e) {
    // Use the default values from table context
  }

  // If no segmentations, don't render
  if (!data?.length) {
    return null;
  }

  // Check if all segments are visible
  const allSegmentsVisible = Object.values(representation?.segments || {}).every(
    segment => segment?.visible !== false
  );

  const Icon = allSegmentsVisible ? (
    <Icons.Hide className="h-6 w-6" />
  ) : (
    <Icons.Show className="h-6 w-6" />
  );

  const allowAddSegment = showAddSegment && !disableEditing;
  const segmentationInfo = data.find(info => info.segmentation?.segmentationId === segmentationId);
  const hasDrawableContourData = hasContourData(
    (segmentationInfo?.segmentation as any)?.representationData?.Contour
  );
  /**
   * "Show in this viewport" is a cross-viewport action:
   * - Source contour exists in another viewport (DE/SE)
   * - Target is the currently active viewport (often DWI-family) which may have *no* contour yet
   *
   * So we expose the button whenever the action is available. The command itself validates that
   * a drawable contour source exists and that the target viewport is appropriate.
   */
  const allowShowInViewport = showAddSegment && !disableEditing && !!onShowSegmentationInViewport;
  const allowApplyTranslation = showAddSegment && !disableEditing && !!onApplyTrenTranslation;

  return (
    <div className="my-px flex h-7 w-full items-center justify-between rounded pl-0.5 pr-7">
      <div className="mt-1 flex flex-1 items-center gap-1">
        {allowAddSegment ? (
          <Button
            size="sm"
            variant="ghost"
            className="pr pl-0.5"
            onClick={() => onSegmentAdd(segmentationId)}
          >
            <Icons.Add />
            {t('Add Segment')}
          </Button>
        ) : null}
        {allowShowInViewport ? (
          <Button
            size="sm"
            variant="ghost"
            className="px-0.5"
            onClick={() => onShowSegmentationInViewport()}
          >
            <Icons.Add />
            {t('Show in this viewport')}
          </Button>
        ) : null}
        {allowApplyTranslation ? (
          <Button
            size="sm"
            variant="ghost"
            className="px-0.5"
            onClick={() => onApplyTrenTranslation()}
          >
            <Icons.Add />
            {t('Apply Translation')}
          </Button>
        ) : null}
      </div>
      <Button
        size="icon"
        variant="ghost"
        onClick={() =>
          onToggleSegmentationRepresentationVisibility(segmentationId, representation?.type)
        }
      >
        {Icon}
      </Button>
      {children}
    </div>
  );
};
